/**
 * CORE-1637 — поправка на часы сервера для расчёта позиции общей практики.
 *
 * Позиция практики у каждого клиента — это «сейчас − отметка сервера»
 * (`updatedAtUnix` кадра хаба, `mediaStartedAtUtc` комнаты). «Сейчас» бралось из
 * `Date.now()`, то есть из часов устройства, и их ошибка один в один уходила в
 * позицию: устройство, у которого часы спешат на секунду, играло на секунду
 * впереди комнаты. На проде такие клиенты есть — событие аналитики с часами
 * клиента на 1 с впереди серверного приёма (Chrome/Windows, 07.10.2026) и на
 * 28 с (01.10.2026), — а порог приёмки по тикету весь равен одной секунде.
 *
 * Сдвиг меряется по кругу «запрос — ответ» (`RequestSync` хаба возвращает время
 * сервера): сервер ответил где-то между отправкой и приёмом, так что ошибка
 * оценки не больше половины времени круга. Поэтому из замеров держим тот, у
 * которого круг короче.
 *
 * Модуль, а не состояние хука: расчёт позиции живёт в чистых функциях
 * (`getCurrentPosition`) и в слушателях, привязанных один раз, а сдвиг — свойство
 * устройства, а не комнаты.
 */

export interface ServerClockSample {
    /** `Date.now()` перед отправкой запроса. */
    sentAtMs: number;
    /** `Date.now()` в момент прихода ответа. */
    receivedAtMs: number;
    /** Время сервера из ответа, мс эпохи. */
    serverUnixMs: number;
}

/**
 * Круг длиннее этого ничего не говорит о часах: ошибка в половину круга сама
 * съела бы порог в секунду. Такой замер лучше пропустить и дождаться следующего
 * (опрос идёт раз в 15 с).
 */
export const SERVER_CLOCK_MAX_ROUND_TRIP_MS = 2_000;

interface Estimate {
    offsetMs: number;
    roundTripMs: number;
}

let best: Estimate | null = null;

/**
 * Учесть замер. Возвращает действующий сдвиг (мс), который надо прибавить к
 * `Date.now()`, чтобы получить время сервера.
 */
export function recordServerClockSample({ sentAtMs, receivedAtMs, serverUnixMs }: ServerClockSample): number {
    const roundTripMs = receivedAtMs - sentAtMs;
    if (!Number.isFinite(serverUnixMs) || serverUnixMs <= 0
        || !Number.isFinite(roundTripMs) || roundTripMs < 0
        || roundTripMs > SERVER_CLOCK_MAX_ROUND_TRIP_MS) {
        return getServerClockOffsetMs();
    }
    const next: Estimate = {
        offsetMs: serverUnixMs - (sentAtMs + receivedAtMs) / 2,
        roundTripMs,
    };
    // Короткий круг точнее длинного. А замер, который расходится с прежним
    // сильнее, чем позволяют обе погрешности вместе, значит, что часы устройства
    // переставили (синхронизация, ручная правка) — прежняя оценка уже не про них.
    const contradicts = best !== null
        && Math.abs(next.offsetMs - best.offsetMs) > (next.roundTripMs + best.roundTripMs) / 2;
    if (best === null || contradicts || next.roundTripMs <= best.roundTripMs) best = next;
    return best.offsetMs;
}

/** Сдвиг часов сервера относительно часов устройства, мс; `0`, пока не мерили. */
export function getServerClockOffsetMs(): number {
    return best?.offsetMs ?? 0;
}

/** «Сейчас» по часам сервера, мс эпохи. До первого замера — часы устройства. */
export function serverNow(): number {
    return Date.now() + getServerClockOffsetMs();
}

/** Только для тестов. */
export function resetServerClock(): void {
    best = null;
}
